import {
  ICustomField,
  IStatusCycle,
} from "../models/commissionAndRepayment.model";
import { IAddRepaymentData } from "../models/epcCustomer.model";
import axiosSetup from "../utils/axiosSetup";

export const fetchAllCommissions = async (
  page = 1,
  limit = 20,
  search?: string,
  filter?: any,
) => {
  try {
    const params = { page, limit, search, filter };
    const response = await axiosSetup.get("Epc/get-all-commissions", {
      params,
    });
    return response.data.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to fetch all commissions";
  }
};

export const updateBulkCommissionStatusApi = async (
  commissionHistoryIds: string[],
  action: "approve" | "reject",
  proofFile?: File | null,
) => {
  try {
    const formData = new FormData();

    commissionHistoryIds.forEach((id) =>
      formData.append("commissionHistoryIds", id),
    );

    formData.append("action", action);

    if (proofFile) {
      formData.append("proof", proofFile);
    }

    const response = await axiosSetup.put(
      "Epc/update-bulk-commission",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || `Failed to ${action} commissions`;
  }
};

export const updateBulkCommissionStatusAccountsApi = async (
  commissionHistoryIds: string[],
  action: "approve" | "reject",
  proofFile?: File | null,
) => {
  try {
    const formData = new FormData();

    commissionHistoryIds.forEach((id) =>
      formData.append("commissionHistoryIds", id),
    );

    formData.append("action", action);

    if (proofFile) {
      formData.append("proof", proofFile);
    }

    const response = await axiosSetup.put(
      "Epc/update-bulk-commission-account",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || `Failed to ${action} commissions`;
  }
};

export const fetchSalesRepCommissionById = async (
  salesRepId: string,
  startDate?: string,
  endDate?: string,
  page = 1,
  limit = 20,
) => {
  let url = `Epc/get-sales-rep-commission/${salesRepId}?page=${page}&limit=${limit}`;

  if (startDate) {
    url += `&startDate=${startDate}`;
  }
  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  const response = await axiosSetup.get(url);
  return response.data.data;
};

export const fetchAccountCommissions = async (
  page = 1,
  limit = 12,
  status: string,
) => {
  try {
    const response = await axiosSetup.get(
      `Epc/get-account-commissions?page=${page}&limit=${limit}&status=${status}`,
    );
    return response.data.data;
  } catch (err: any) {
    throw err.response?.data?.message;
  }
};

export const fetchAllRepayments = async (
  page = 1,
  limit = 10,
  from: string,
  search?: string,
  status?: string,
) => {
  try {
    const params = { page, limit, search, status, from };
    const response = await axiosSetup.get("Epc/get-all-repayments", {
      params,
    });
    return response.data.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to fetch sales rep repayments";
  }
};

export const updateBulkRepaymentStatusApi = async (
  repaymentHistoryIds: string[],
  action: "approve" | "reject",
  remark?: string,
  proofFile?: File | null,
  statusType?: "cse" | "account",
) => {
  try {
    const formData = new FormData();

    repaymentHistoryIds.forEach((id) =>
      formData.append("repaymentHistoryIds", id),
    );

    formData.append("action", action);

    if (statusType) {
      formData.append("statusType", statusType);
    }

    if (remark) {
      formData.append("remark", remark);
    }

    if (proofFile) {
      formData.append("proof", proofFile);
    }

    const response = await axiosSetup.put(
      "Epc/update-bulk-repayment",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || `Failed to ${action} repayments`;
  }
};

export const fetchRepaymentsBySalesRep = async (
  salesRepId: string,
  page = 1,
  limit = 20,
  startDate?: string,
  endDate?: string,
  search?: string,
  status?: IStatusCycle,
) => {
  try {
    const response = await axiosSetup.get(`Epc/get-repayments-by-sales-rep`, {
      params: { salesRepId, page, limit, startDate, endDate, search, status },
    });
    return response.data.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to fetch repayments";
  }
};

export const updateRepaymentCustomFieldApi = async (
  repaymentHistoryId: string,
  customFields: ICustomField[],
) => {
  try {
    const response = await axiosSetup.patch(
      "Epc/update-repayment-custom-field",
      {
        repaymentHistoryId,
        customFields,
      },
    );
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to update custom fields";
  }
};

export const updateCommissionApi = async (
  commissionId: string,
  commissionHistoryId: string,
  data: {
    salesRepCommission?: number;
    excessAmount?: number;
    adjustmentAmount?: number;
    excessAdjustAmount?: number;
  },
) => {
  try {
    const response = await axiosSetup.patch(
      `Epc/update-sales-rep-commission?commissionId=${commissionId}&commissionHistoryId=${commissionHistoryId}`,
      { ...data },
    );
    return response.data.data;
  } catch (err: any) {
    throw (
      err.response?.data?.message || "Failed to update commission adjustment"
    );
  }
};

export const addRepaymentApi = async (data: IAddRepaymentData) => {
  try {
    const response = await axiosSetup.post("Epc/add-repayment", data);
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to add repayment";
  }
};

export const updateRepaymentApi = async (
  repaymentId: string,
  repaymentHistoryId: string,
  data: {
    fileCharge?: number;
    portalCharge?: number;
    customFields?: ICustomField[];
  },
) => {
  try {
    const response = await axiosSetup.patch(
      `Epc/update-repayment?repaymentId=${repaymentId}&repaymentHistoryId=${repaymentHistoryId}`,
      { ...data },
    );
    return response.data;
  } catch (err: any) {
    throw (
      err.response?.data?.message || "Failed to update commission adjustment"
    );
  }
};

export const addSalesRepCommissionApi = async (data: {
  salesRepId: string;
  customerId: string;
}) => {
  try {
    const response = await axiosSetup.post(
      "Epc/add-sales-rep-commission",
      data,
    );
    return response.data;
  } catch (err: any) {
    throw err.response?.data?.message || "Failed to add commission";
  }
};
